/**
 * MFA Challenge page — used during the OAuth flow.
 *
 * When the /api/oauth/authorize endpoint detects that a client (e.g. Money)
 * requires MFA and the user hasn't verified recently, it redirects here:
 *
 *   /mfa-challenge?client_id=nacho-money&redirect_uri=...&state=...
 *
 * This page:
 * 1. Shows a TOTP code input
 * 2. Calls POST /api/auth/mfa/verify-session to stamp mfa_verified_at
 * 3. Redirects back to /api/oauth/authorize with the same params + access_token
 *    so the authorize endpoint can now issue the auth code
 *
 * The user is already logged into Hub (has tokens) — they just need to
 * prove they have their authenticator.
 */

import { useState } from 'react';
import { useSearchParams } from 'react-router-dom';

import { api, getAccessToken } from '../api/client.js';
import { ApiError } from '../api/client.js';
import { usePageTitle } from '../hooks/usePageTitle.js';
import './LoginPage.css';   // reuse the login card styles


export default function MfaChallengePage() {
  usePageTitle('Verify identity');

  const [searchParams] = useSearchParams();
  const [code, setCode] = useState('');
  const [submitting, setSubmitting] = useState(false);
  const [error, setError] = useState('');

  const canSubmit = code.length === 6 && !submitting;

  // Read the OAuth params that the authorize endpoint forwarded
  const client_id = searchParams.get('client_id');
  const redirect_uri = searchParams.get('redirect_uri');
  const response_type = searchParams.get('response_type');
  const state = searchParams.get('state');
  const scope = searchParams.get('scope');
  const nonce = searchParams.get('nonce');

  async function handleSubmit(e) {
    e.preventDefault();
    if (!canSubmit) return;

    setSubmitting(true);
    setError('');

    try {
      // Stamp mfa_verified_at on the user
      await api.post('/api/auth/mfa/verify-session', { code });

      // Build the authorize URL with the same OAuth params + access token
      const params = new URLSearchParams();
      if (client_id) params.set('client_id', client_id);
      if (redirect_uri) params.set('redirect_uri', redirect_uri);
      if (response_type) params.set('response_type', response_type);
      if (state) params.set('state', state);
      if (scope) params.set('scope', scope);
      if (nonce) params.set('nonce', nonce);

      const token = getAccessToken();
      if (token) params.set('access_token', token);

      // Full-page navigation — the authorize endpoint will redirect
      // to the module's callback with the auth code
      window.location.href = `/api/oauth/authorize?${params.toString()}`;
    } catch (err) {
      if (err instanceof ApiError) {
        setError(err.detail || 'Invalid code. Try again.');
      } else {
        setError('Something went wrong. Check your connection and try again.');
      }
      setCode('');
    } finally {
      setSubmitting(false);
    }
  }

  return (
    <div className="login-page">
      <header className="login-top" aria-hidden="true" />

      <main className="login-stage">
        <section className="login-card">
          <div className="login-card-head">
            <span className="login-sticker">🔐 verification required</span>
            <h1 className="login-title">Confirm it's you</h1>
            <p className="login-sub">
              this app requires extra verification
            </p>
          </div>

          {error && (
            <div className="login-error" role="alert">
              <span aria-hidden="true">⚠</span>
              <span>{error}</span>
            </div>
          )}

          <form onSubmit={handleSubmit} noValidate>
            <div className="login-field">
              <label className="login-label" htmlFor="mfa-code">
                <span>6-digit code</span>
              </label>
              <input
                id="mfa-code"
                className="login-mfa-input"
                type="text"
                inputMode="numeric"
                autoComplete="one-time-code"
                pattern="[0-9]{6}"
                maxLength={6}
                placeholder="000000"
                value={code}
                onChange={(e) => {
                  const v = e.target.value.replace(/\D/g, '').slice(0, 6);
                  setCode(v);
                }}
                autoFocus
              />
            </div>

            <button
              type="submit"
              className="login-submit"
              disabled={!canSubmit}
            >
              {submitting ? 'verifying…' : 'verify  →'}
            </button>
          </form>
        </section>
      </main>

      <div className="login-tagline">
        <strong>NachoFiles</strong> — the reason the chicken crossed the road
      </div>
    </div>
  );
}
